import { useWrapAsync } from "@paroicms/front-app-log";
import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import type { KpiInputValues } from "../../../../../../api-requests/settings";
import { updateSettingsAddReferenceKpis } from "../../../../../../api/application/settings";
import type { GeneratedReference } from "../../../../../../api/infrastructure/reference.formatter";
import type { TableForm } from "../../../../../../api/infrastructure/table-appendix.formatter";
import { appLog } from "../../../../../../context";
import { showToast } from "../../../../../shared/global-tools/toast-system";
import NoDataMessage from "../../../../utils/NoDataMessage/NoDataMessage";
import type { KpiInputExtended } from "../DefineKpi";
import KpiCard from "../KpiCard/KpiCard";
import DefineChartKPI from "./DefineChartKPI/DefineChartKPI";
import "./DefineKpiContent.scss";

export default function DefineKpiContent({
  tableForm,
  selectedTableId,
  kpiColumns,
  kpiInputValuesExtended,
  selectedReference,
  onSuccess,
  onKpiInputValuesChange,
}: {
  tableForm: TableForm[];
  selectedTableId: number;
  selectedReference?: GeneratedReference;
  kpiColumns: TableForm[] | undefined;
  kpiInputValuesExtended: KpiInputExtended[] | undefined;
  onSuccess?(): void;
  onKpiInputValuesChange(kpiInputValuesExtended: KpiInputExtended[]): void;
  refreshKpiInputValues(): void;
}) {
  const wrapAsync = useWrapAsync();
  const [loading, setLoading] = useState(false);
  const [chartKpiName, setChartKpiName] = useState("");
  const [chartKpiProperty, setChartKpiProperty] = useState<string>();

  const handleReferenceKpiAddition = wrapAsync(async () => {
    if (
      selectedReference === undefined ||
      !kpiInputValuesExtended ||
      kpiInputValuesExtended.length === 0 ||
      !chartKpiProperty
    ) {
      return;
    }
    const result = checkKPInputValues(kpiInputValuesExtended, tableForm);
    if (result.hasError) {
      showToast("Erreur définition de KPI", { severity: "warn" });
      onKpiInputValuesChange(result.newKpiInputValues);
      return;
    }
    setLoading(true);
    try {
      await updateSettingsAddReferenceKpis({
        referenceId: selectedReference.id,
        kpis: kpiInputValuesExtended.map((k) => ({
          tableId: selectedTableId,
          name: k.name,
          property: k.property,
          value: k.value,
        })),
        histogramKpi: {
          tableId: selectedTableId,
          name: chartKpiName,
          property: chartKpiProperty,
        },
      });
      showToast("Mise à jour référentiel kpis réussie", {
        severity: "success",
      });
      onSuccess?.();
    } catch (error) {
      appLog.error(error);
      showToast("Echec mise à jour référentiel kpis", {
        severity: "error",
        sticky: true,
      });
    }
    setLoading(false);
  });
  const editKpi = (id: string, newVal: KpiInputValues) => {
    if (!kpiInputValuesExtended) return;
    const index = kpiInputValuesExtended.findIndex((k) => k.id === id);
    if (index === -1) return;
    kpiInputValuesExtended[index] = {
      ...newVal,
      id,
    };
    onKpiInputValuesChange(kpiInputValuesExtended);
  };

  useEffect(() => {
    const validFields = kpiColumns;
    setChartKpiProperty(validFields?.[0].name);
  }, [selectedTableId]);

  if (!kpiColumns || kpiColumns.length === 0 || !kpiInputValuesExtended) {
    return (
      <div className="mt-6">
        <NoDataMessage message="Aucun champ valide pour les KPI" />
      </div>
    );
  }
  return (
    <div className="DefineKpiContent mt-4">
      {/* TODO: remove if unnecessary */}
      {/* <div className="flex align-content-end">
        <Button
          icon="pi pi-refresh"
          rounded
          text
          className="p-outlined-primary IconOnlyBtn"
          onClick={refreshKpiInputValues}
        />
      </div> */}
      {kpiInputValuesExtended.length > 0 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleReferenceKpiAddition();
          }}
        >
          <div className="DefineKpiContent-cardList">
            {kpiInputValuesExtended.map((kpi) => (
              <KpiCard key={kpi.id} kpi={kpi} kpiColumns={kpiColumns} editKpi={editKpi} />
            ))}
          </div>

          <DefineChartKPI
            name={chartKpiName}
            property={chartKpiProperty}
            kpiColumns={kpiColumns}
            onNameChange={(val) => setChartKpiName(val)}
            onPropertyChange={(val) => setChartKpiProperty(val)}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
            }}
            className="mt-6"
          >
            <Button
              type="submit"
              className="Btn lg primary"
              label="Valider"
              loading={loading}
              disabled={selectedReference === undefined || kpiInputValuesExtended.length === 0}
            />
          </div>
        </form>
      )}
    </div>
  );
}

function checkKPInputValues(
  kpiInputValues: KpiInputExtended[],
  tableColumns: TableForm[],
): {
  newKpiInputValues: KpiInputExtended[];
  hasError: boolean;
} {
  let hasError = false;
  const newKpiInputValues = kpiInputValues.map((v) => {
    const foundField = tableColumns.find((f) => f.name === v.property);
    if (!foundField) {
      throw new Error("Schema field not found");
    }
    if (foundField.required && v.value === null) {
      hasError = true;
      return {
        ...v,
        error: "Valeur requise",
      };
    }
    return v;
  });
  return {
    newKpiInputValues,
    hasError,
  };
}
